// Plan de Emergencias v2 por capítulos (PE-C1, estructura de Kevin). Solo pantalla y orden: ningún cálculo cambia.
//  - 8 capítulos con sus subapartados, una pestaña final opcional "Continuidad del negocio y póliza" y la pestaña fija
//    "Revisión y aprobación" (resultados, plan de acción y versiones), fuera de los 8 capítulos.
//  - "Completo": un capítulo está completo cuando no le falta nada de lo que HOY exige la aprobación para esa parte.
//    Se calcula con faltantesAprobacion (pe-versiones.ts) tal cual, separando sus mensajes por capítulo; las firmas
//    son del modal de aprobación y no de un capítulo. Los capítulos sin requisitos para aprobar NO cuentan como
//    completos: se muestran como "Sin requisitos aún" y el avance solo cuenta los capítulos con requisitos.
//  - Vista previa y PDF en el orden de los capítulos: se reagrupan las secciones de armarVistaPrevia (mismo contenido);
//    la continuidad del negocio va al final y solo si tiene datos.

import type { FilaAccion } from './pe-acciones';
import type { FilaPlan } from './pe-datos';
import type { SectorEnPantalla } from './pe-paso2';
import type { Evaluacion } from './pe-paso3';
import { faltantesAprobacion, PREFIJO_CAPITULO_1, PREFIJO_CAPITULO_2, ROLES_FIRMA } from './pe-versiones';
import { armarVistaPrevia, continuidadDelPlan, type DatosVista, type Linea, type SeccionVista } from './pe-vista-previa';
import { transferenciaDe } from './pe-continuidad';

export type NumCapitulo = 1 | 2 | 3 | 4 | 5 | 6 | 7 | 8;
export type IdVista = NumCapitulo | 'continuidad' | 'revision';
export type SubRevision = 'resultados' | 'acciones' | 'aprobacion';

export interface Subapartado {
  id: string;                 // ancla estable en la pantalla
  titulo: string;
  proximamente?: string;      // todavía no existe: se muestra el aviso "Próximamente" (PE-C2 y PE-C3), no se construye
  // Secciones de la vista previa (armarVistaPrevia) que le corresponden. 'N|a,b' = solo las líneas de la sección N que
  // empiezan con a o b (un número como '4.1' o un texto como 'Carga de fuego'); 'N|-a,b' = las que no empiezan con ellas.
  secciones?: string[];
}
export interface Capitulo { numero: NumCapitulo; titulo: string; corto: string; subapartados: Subapartado[] }

export const PROXIMAMENTE = 'Próximamente';

export const CAPITULOS: Capitulo[] = [
  { numero: 1, titulo: 'Datos generales del establecimiento', corto: 'Datos generales', subapartados: [
    { id: 'informacion-general', titulo: 'Información general', secciones: ['1'] },
    { id: 'instalaciones', titulo: 'Instalaciones y superficies' },
    { id: 'poblacion', titulo: 'Población' },
    { id: 'situacion', titulo: 'Situación frente a emergencias' },
    { id: 'justificacion', titulo: 'Justificación y objetivos', proximamente: 'Justificación y objetivos del plan.' },
    { id: 'politica', titulo: 'Política de emergencias', proximamente: 'Política de emergencias con comentarios y aprobación.' },
  ] },
  { numero: 2, titulo: 'Identificación de factores de riesgo / análisis de riesgos y amenazas', corto: 'Factores de riesgo', subapartados: [
    { id: 'sectores', titulo: 'Sectores y carga de fuego', secciones: ['2', '4|4.1,Carga de fuego,4.3'] },
    { id: 'meseri', titulo: 'Riesgo de incendio (MESERI)', secciones: ['4|4.2'] },
    { id: 'amenazas', titulo: 'Amenazas naturales y vulnerabilidad', secciones: ['3', '6'] },
    { id: 'acciones-preventivas', titulo: 'Acciones preventivas y de control', secciones: ['5'] },
  ] },
  { numero: 3, titulo: 'Recursos y capacidades', corto: 'Recursos', subapartados: [
    { id: 'verificacion', titulo: 'Verificación normativa (extintores, iluminación y señalización)', secciones: ['4|4.4', '7', '8'] },
    { id: 'equipos', titulo: 'Registro de equipos de emergencia', proximamente: 'Registro de equipos con su mantenimiento (junto con el módulo de Mantenimiento).' }, // Fase C
  ] },
  { numero: 4, titulo: 'Organización y responsabilidades (brigada)', corto: 'Organización', subapartados: [
    { id: 'brigada', titulo: 'Brigada de emergencia', secciones: ['12'] },
    { id: 'responsabilidades', titulo: 'Organigrama y responsabilidades', proximamente: 'Nombres, cargos y roles de la brigada y del comité.' },
  ] },
  { numero: 5, titulo: 'Procedimientos de respuesta', corto: 'Procedimientos', subapartados: [
    { id: 'alarma', titulo: 'Protocolo de alarma y comunicaciones', secciones: ['9'], proximamente: 'Texto base del protocolo (DE 255, AM 1257 y RTQ).' },
    { id: 'grados', titulo: 'Grados de emergencia y actuación', secciones: ['10', '11', '16'], proximamente: 'Diagrama de actuación por grado.' },
  ] },
  { numero: 6, titulo: 'Evacuación y puntos de encuentro', corto: 'Evacuación', subapartados: [
    { id: 'aforo', titulo: 'Aforo y anchos de egreso', secciones: ['4|4.5', '13'] },
    { id: 'rset', titulo: 'Tiempo de salida (Togawa) y RSET', secciones: ['14|14.1,14.2'] },
    { id: 'punto-encuentro', titulo: 'Punto de encuentro', secciones: ['14|-14.1,14.2'] },
  ] },
  { numero: 7, titulo: 'Capacitación y simulacros', corto: 'Simulacros', subapartados: [
    { id: 'simulacros', titulo: 'Simulacros', secciones: ['15'] },
    { id: 'capacitacion', titulo: 'Capacitación de la brigada' },
  ] },
  { numero: 8, titulo: 'Anexos y planos', corto: 'Anexos', subapartados: [
    { id: 'mapa', titulo: 'Mapa de riesgos, recursos y evacuación', secciones: ['Anexo'], proximamente: 'Subir el mapa y los planos de la instalación.' },
    { id: 'cronograma', titulo: 'Cronograma de implementación', secciones: ['17'] },
    { id: 'pendientes', titulo: 'Pendientes "por validar"' },
  ] },
];

export const TITULO_CONTINUIDAD_PESTANA = 'Continuidad del negocio y póliza';
export const AVISO_CONTINUIDAD = 'Opcional. DE 394 Art. 5 núm. 3. No bloquea la aprobación del plan.';
export const TITULO_REVISION = 'Revisión y aprobación';
export const SUB_REVISION: { id: SubRevision; titulo: string }[] = [
  { id: 'resultados', titulo: 'Resultados (semáforo)' }, { id: 'acciones', titulo: 'Plan de acción' }, { id: 'aprobacion', titulo: 'Versiones y aprobación' },
];

export const capitulo = (n: NumCapitulo) => CAPITULOS[n - 1];

// Evaluación del paso 3 → capítulo donde se edita (las entradas siguen siendo una sola: se guardan juntas).
export function capituloDeEvaluacion(clave: string): NumCapitulo {
  if (clave.startsWith('meseri') || clave === 'amenaza_vulnerabilidad' || clave === 'carga_fuego') return 2;
  if (clave === 'verificacion') return 3;
  return 6; // aforo_egreso, rset, punto_encuentro
}

// Las trazas dicen en qué paso del asistente anterior se completan (pe-paso3, sin cambios): se traduce al capítulo.
export function vistaDePaso(paso: number, claveEvaluacion?: string): { vista: IdVista; sub?: SubRevision } {
  if (paso <= 1) return { vista: 1 };
  if (paso === 2) return { vista: 2 };
  if (paso === 3) return { vista: claveEvaluacion ? capituloDeEvaluacion(claveEvaluacion) : 2 };
  if (paso === 5) return { vista: 'revision', sub: 'acciones' };
  return { vista: 'revision', sub: paso === 4 ? 'resultados' : 'aprobacion' };
}

export function etiquetaVista(v: IdVista, sub?: SubRevision): string {
  if (v === 'continuidad') return TITULO_CONTINUIDAD_PESTANA;
  if (v === 'revision') return `${TITULO_REVISION}${sub ? ` › ${SUB_REVISION.find(s => s.id === sub)?.titulo}` : ''}`;
  return `capítulo ${v} (${capitulo(v).corto})`;
}

// Textos de faltantesAprobacion con el nombre de la pantalla nueva (los requisitos son exactamente los mismos). Desde
// el ajuste de PE-C1 faltantesAprobacion ya nombra el capítulo; esto solo traduce textos viejos que pudieran llegar.
export function textoConCapitulos(s: string): string {
  return s.replace(/^Paso 1:/, 'Capítulo 1 (Datos generales):').replace(/^Paso 2:/, 'Capítulo 2 (Sectores y carga de fuego):')
    .replace('(paso 1)', '(capítulo 1)').replace('(paso 5)', `(${TITULO_REVISION} › Plan de acción)`);
}

export interface DatosCapitulos { plan: FilaPlan | null; sectores: SectorEnPantalla[]; evaluaciones: Evaluacion[]; acciones: FilaAccion[] }

// Firmas "completas" para que faltantesAprobacion no pida firmas: las firmas no son de ningún capítulo.
const FIRMAS_OK = ROLES_FIRMA.map(rol => ({ rol, nombre: '—', cargo: '', conTrazo: true }));
const NUMEROS: NumCapitulo[] = [1, 2, 3, 4, 5, 6, 7, 8];
export const CAPITULOS_CON_REQUISITOS: NumCapitulo[] = [1, 2, 3, 6];
export const tieneRequisitos = (n: NumCapitulo): boolean => CAPITULOS_CON_REQUISITOS.includes(n);
export const SIN_REQUISITOS = 'Sin requisitos aún';

/** Lo que falta para aprobar, separado por capítulo (mismos requisitos y mismos textos del modal de aprobación). */
export function faltantesPorCapitulo(d: DatosCapitulos): Record<NumCapitulo, string[]> {
  const r = Object.fromEntries(NUMEROS.map(n => [n, [] as string[]])) as Record<NumCapitulo, string[]>;
  if (!d.plan) {
    const m = faltantesAprobacion({ ...d, evaluaciones: [], acciones: [], firmas: FIRMAS_OK });
    for (const n of NUMEROS) r[n] = [...m];
    return r;
  }
  const base = faltantesAprobacion({ plan: d.plan, sectores: d.sectores, evaluaciones: [], acciones: [], firmas: FIRMAS_OK });
  r[1] = base.filter(s => s.startsWith(PREFIJO_CAPITULO_1));
  r[2] = base.filter(s => s.startsWith(PREFIJO_CAPITULO_2));
  for (const n of [2, 3, 6] as NumCapitulo[]) {
    const evs = d.evaluaciones.filter(e => capituloDeEvaluacion(e.clave) === n);
    r[n].push(...faltantesAprobacion({ plan: d.plan, sectores: d.sectores, evaluaciones: evs, acciones: d.acciones, firmas: FIRMAS_OK })
      .filter(s => s.startsWith('Incumplimiento sin acción')));
  }
  return r;
}

/** Avance: solo los capítulos con requisitos (los demás no cuentan ni como completos ni como incompletos). */
export function resumenProgreso(d: DatosCapitulos): {
  completos: NumCapitulo[]; total: number; sinRequisitos: NumCapitulo[]; faltantes: Record<NumCapitulo, string[]>; texto: string;
} {
  const faltantes = faltantesPorCapitulo(d);
  const completos = CAPITULOS_CON_REQUISITOS.filter(n => faltantes[n].length === 0);
  const total = CAPITULOS_CON_REQUISITOS.length;
  return { completos, total, sinRequisitos: NUMEROS.filter(n => !tieneRequisitos(n)), faltantes,
    texto: `${completos.length} de ${total} capítulos con requisitos completos` };
}

// ── Vista previa y PDF por capítulos ─────────────────────────────────────────

export interface SubseccionCapitulo { id: string; numero: string; titulo: string; lineas: Linea[]; porValidar: number; secciones: string[] }
export interface CapituloVista { numero: string; titulo: string; subsecciones: SubseccionCapitulo[]; porValidar: number }

// Las líneas de la vista llevan la numeración vieja del documento (4.1, 12.2…): en el orden nuevo se quita.
const sinNumeroViejo = (l: Linea): Linea => ({ ...l, texto: l.texto.replace(/^\d{1,2}\.\d\s+/, '') });

export const tieneContinuidad = (p: FilaPlan | null): boolean => {
  if (continuidadDelPlan(p)) return true;
  const t = p ? transferenciaDe(p) : null;
  return !!t && Object.values(t).some(v => v !== null && v !== '' && v !== false);
};

/**
 * Secciones de armarVistaPrevia reagrupadas en los capítulos (portada aparte). La continuidad (18) y la transferencia
 * (19) van al final, como "Continuidad del negocio y póliza", solo si tienen datos; las firmas, después.
 */
export function armarVistaPorCapitulos(d: DatosVista): { portada: SeccionVista; capitulos: CapituloVista[]; firmas: SeccionVista } {
  const secciones = armarVistaPrevia(d);
  const de = (n: string) => secciones.find(s => s.numero === n);
  const parte = (sel: string): SeccionVista | null => {
    const [n, filtro] = sel.split('|');
    const x = de(n);
    if (!x || !filtro) return x ?? null;
    const excluir = filtro.startsWith('-');
    const prefijos = filtro.replace(/^-/, '').split(',');
    // Un prefijo numérico ('4.1') se compara con su espacio; uno de texto ('Carga de fuego'), tal cual.
    const con = (l: Linea) => prefijos.some(pre => l.texto.startsWith(/^\d/.test(pre) ? `${pre} ` : pre));
    return { ...x, lineas: x.lineas.filter(l => (excluir ? !con(l) : con(l))) };
  };
  const capitulos: CapituloVista[] = CAPITULOS.map(c => {
    const subsecciones = c.subapartados.map((s, i) => {
      const propias = (s.secciones ?? []).map(parte).filter((x): x is SeccionVista => !!x);
      const lineas = propias.flatMap(x => x.lineas.map(sinNumeroViejo));
      const vacias = lineas.length ? lineas
        : [{ texto: s.proximamente ? `${PROXIMAMENTE}: ${s.proximamente}` : 'Ver el detalle en la pantalla del capítulo.', pendiente: true }];
      const porValidar = lineas.filter(l => l.estado === 'por_validar').length;
      return { id: s.id, numero: `${c.numero}.${i + 1}`, titulo: s.titulo, lineas: vacias, porValidar, secciones: s.secciones ?? [] };
    });
    return { numero: String(c.numero), titulo: c.titulo, subsecciones, porValidar: subsecciones.reduce((a, s) => a + s.porValidar, 0) };
  });
  if (tieneContinuidad(d.plan)) {
    const sub = (['18', '19'] as const).map(de).filter((x): x is SeccionVista => !!x)
      .map((x, i) => ({ id: `continuidad-${x.numero}`, numero: `9.${i + 1}`, titulo: x.titulo, lineas: x.lineas, porValidar: x.porValidar, secciones: [x.numero] }));
    capitulos.push({ numero: '9', titulo: TITULO_CONTINUIDAD_PESTANA, subsecciones: sub, porValidar: sub.reduce((a, s) => a + s.porValidar, 0) });
  }
  return { portada: de('Portada') as SeccionVista, capitulos, firmas: de('Firmas') as SeccionVista };
}
